/**
 * Thumbs up / down under a finished assistant answer.
 * Thumbs down opens a one-line "What was wrong?" field. The choice can be changed.
 */

import React, { useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip } from '@mui/material';
import {
  ThumbUpAltOutlined as ThumbUpOutlinedIcon,
  ThumbUpAlt as ThumbUpIcon,
  ThumbDownAltOutlined as ThumbDownOutlinedIcon,
  ThumbDownAlt as ThumbDownIcon,
} from '@mui/icons-material';
import { submitFeedback } from '../services/api.service';

interface MessageFeedbackProps {
  messageId: string;
}

const MessageFeedback: React.FC<MessageFeedbackProps> = ({ messageId }) => {
  const [rating, setRating] = useState<'up' | 'down' | null>(null);
  const [editingComment, setEditingComment] = useState(false);
  const [comment, setComment] = useState('');
  const [failed, setFailed] = useState(false);

  const send = async (next: 'up' | 'down', text?: string) => {
    try {
      await submitFeedback(messageId, next, text);
      setRating(next);
      setFailed(false);
    } catch (error) {
      console.error('Failed to send feedback:', error);
      setFailed(true);
    }
  };

  const handleUp = async () => {
    setEditingComment(false);
    await send('up');
  };

  const handleDown = async () => {
    setEditingComment(true);
    await send('down');
  };

  const handleSendComment = async () => {
    await send('down', comment.trim());
    setEditingComment(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
      <Tooltip title="Good answer">
        <IconButton size="small" onClick={handleUp} aria-label="thumbs up" sx={{ opacity: 0.7 }}>
          {rating === 'up' ? <ThumbUpIcon fontSize="small" color="primary" /> : <ThumbUpOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Bad answer">
        <IconButton size="small" onClick={handleDown} aria-label="thumbs down" sx={{ opacity: 0.7 }}>
          {rating === 'down' ? <ThumbDownIcon fontSize="small" color="error" /> : <ThumbDownOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      {editingComment && (
        <>
          <TextField
            size="small"
            variant="standard"
            placeholder="What was wrong? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            sx={{ minWidth: 220 }}
          />
          <Button size="small" onClick={handleSendComment}>
            Send
          </Button>
        </>
      )}
      {failed && (
        <Box component="span" sx={{ fontSize: 12, color: 'error.main' }}>
          Could not send feedback
        </Box>
      )}
    </Box>
  );
};

export default MessageFeedback;
